import {
  Badge, Button, Card, ChildCard, EmptyState, ErrorState, LoadingState, PageHeader, Permissions, Row,
  Select, Stack,
  invalidateCareDay, useApi, useI18n, useMemberSwitch, useSession,
  type AttendanceStatus, type ClassDaySummaryResponse, type Presence,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMyClasses } from '../myClasses';
import { childName } from '../names';

/**
 * The teacher's class list (FR-STF-01, FR-STF-02).
 *
 * A teacher sees only the children in the classes they are assigned to — enforced by the server, so this
 * screen asks for the roll and gets back exactly their children. Where they hold more than one class, a
 * switcher appears; where they hold one, it does not. Either way the screen opens on a roll: the one class,
 * or the first they are assigned to where the list holds more (a manager who also leads a room). A room the
 * teacher sees only because their own child is in it is not offered: they are a parent there, and record
 * nothing on its board (Q52).
 *
 * Each row carries today's presence and the care entries still missing, which is the whole point of the
 * board: the gaps have to be visible at a glance rather than found by opening twenty children one by one. A
 * sleeping child is marked, a day waiting for review can be published from its row, and "record for several"
 * turns the roll into a selection for one entry recorded for them all (FR-STF-05).
 */
export function MyClass() {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t, tn, formatNumber, formatTime } = useI18n();
  const { can } = useSession();
  const reviewRequired = useMemberSwitch('daily.review_required');
  const [classId, setClassId] = useState<string>('');
  const [selected, setSelected] = useState<Set<number> | null>(null);

  // The opening class and the switcher both come from one page of the classes in the teacher's reach.
  const { classes, mine: myClasses, opening } = useMyClasses();
  const activeClassId = classId || (opening ? String(opening.id) : '');

  const board = useQuery({
    queryKey: ['class-day', activeClassId],
    queryFn: () => api.daily.forClass(Number(activeClassId)),
    enabled: activeClassId !== '',
  });

  const publish = useMutation({
    mutationFn: (childId: number) => api.daily.publish(childId),
    onSuccess: () => invalidateCareDay(queryClient),
  });

  if (classes.isPending) return <LoadingState />;
  if (classes.isError) return <ErrorState error={classes.error} onRetry={() => void classes.refetch()} />;

  if (myClasses.length === 0) {
    return <EmptyState icon="🧩" title={t('class.none')} />;
  }

  const current = myClasses.find((klass) => String(klass.id) === activeClassId);
  const rows = board.data ?? [];

  const toggle = (childId: number) => setSelected((current) => {
    const next = new Set(current ?? []);
    if (next.has(childId)) next.delete(childId); else next.add(childId);
    return next;
  });

  return (
    <Stack>
      <PageHeader
        title={current?.name ?? t('class.title')}
        actions={rows.length > 1 && (selected === null
          ? <Button variant="secondary" onClick={() => setSelected(new Set())}>{t('bulk.start')}</Button>
          : <Button variant="ghost" onClick={() => setSelected(null)}>{t('action.cancel')}</Button>)}
      />

      {myClasses.length > 1 && (
        <Card>
          <label className="dg-field">
            <span className="dg-field__label">{t('class.switch')}</span>
            <Select value={activeClassId} onChange={(event) => { setClassId(event.target.value); setSelected(null); }}>
              <option value="">—</option>
              {myClasses.map((klass) => (
                <option key={klass.id} value={klass.id}>{klass.name}</option>
              ))}
            </Select>
          </label>
        </Card>
      )}

      {current && (
        <Row>
          <Badge tone="neutral">
            {t('class.summary', {
              enrolled: formatNumber(current.activeChildren),
              capacity: formatNumber(current.capacity),
            })}
          </Badge>
        </Row>
      )}

      {selected !== null && (
        <Card title={t('bulk.title')} hint={t('bulk.hint')}>
          <Row className="dg-row--wrap">
            <Badge tone="neutral">{t('bulk.selected', { count: formatNumber(selected.size) })}</Badge>
            <Button variant="ghost" onClick={() => setSelected(new Set(rows.map((row) => row.childId)))}>
              {t('bulk.selectAll')}
            </Button>
            <Button variant="ghost" disabled={selected.size === 0} onClick={() => setSelected(new Set())}>
              {t('bulk.clear')}
            </Button>
            <Button
              disabled={selected.size === 0}
              onClick={() => navigate(`/group?children=${[...selected].join(',')}`)}
            >
              {tn('bulk.record', selected.size)}
            </Button>
          </Row>
        </Card>
      )}

      {board.isError && <ErrorState error={board.error} onRetry={() => void board.refetch()} />}
      {publish.isError && <ErrorState error={publish.error} />}
      {board.isPending && activeClassId !== '' && <LoadingState />}

      {board.data?.length === 0 && <EmptyState icon="🧒" title={t('class.empty')} />}

      {/* With more than one class and none chosen the board asks for nothing, so without this the screen
          is the switcher and then nothing at all — which reads as a broken page rather than a prompt. */}
      {activeClassId === '' && <EmptyState icon="🏫" title={t('class.switch')} />}

      <Stack>
        {rows.map((row) => {
          const name = childName(row);
          const chosen = selected?.has(row.childId) ?? false;
          const draft = reviewRequired && row.status === 'Draft' && row.recordedKinds.length > 0;

          return (
            <Stack key={row.childId} tight>
              <ChildCard
                child={{
                  id: row.childId,
                  firstName: row.firstName,
                  lastName: row.lastName,
                  preferredName: row.preferredName,
                  dateOfBirth: '',
                  gender: 'Female',
                  classId: current?.id ?? null,
                  className: current?.name ?? null,
                  status: 'Active',
                  allergyMarks: row.allergyMarks,
                }}
                // In a selection the row chooses the child rather than opening them.
                {...(selected === null
                  ? { to: `/child/${row.childId}` }
                  : { onSelect: () => toggle(row.childId) })}
                photoUrl={row.photoUrl}
                presence={presenceOf(row.attendance.status, row.attendance.checkOutAt)}
                meta={(
                  <>
                    {row.asleepSince && <Badge tone="info">{t('class.asleep', { time: formatTime(row.asleepSince) })}</Badge>}
                    <MissingEntries row={row} />
                  </>
                )}
                trailing={selected !== null && (
                  <span>
                    <span aria-hidden="true">{chosen ? '☑' : '☐'}</span>
                    <span className="dg-visually-hidden">{chosen ? t('bulk.isChosen', { name }) : t('bulk.choose', { name })}</span>
                  </span>
                )}
              />
              {draft && selected === null && (
                <Row>
                  <Badge tone="warning">{t('class.draft')}</Badge>
                  {can(Permissions.daily.publish) && (
                    <Button
                      variant="secondary"
                      busy={publish.isPending && publish.variables === row.childId}
                      onClick={() => publish.mutate(row.childId)}
                    >
                      {t('class.publish')}
                    </Button>
                  )}
                </Row>
              )}
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );

  /**
   * What is still missing for this child today.
   *
   * Rendered as the kinds not yet recorded rather than as a tick-list of everything: a teacher scanning
   * the room wants the short list of what is left, not the long list of what is done.
   *
   * A child who has not been checked in has nothing missing and nothing done: the presence label says why, and
   * a tick would read as a day recorded in full (GAPS 1.11).
   */
  function MissingEntries({ row }: { row: ClassDaySummaryResponse }) {
    if (row.attendance.checkInAt === null) return null;

    if (row.missingKinds.length === 0) {
      return <Badge tone="success">✓</Badge>;
    }

    return (
      <span className="dg-row dg-row--wrap">
        {row.missingKinds.map((kind) => (
          <Badge key={kind} tone="neutral">{t(`care.kind.${kind}`)}</Badge>
        ))}
      </span>
    );
  }
}

/** The server's attendance status, as the four states the presence label renders. */
function presenceOf(status: AttendanceStatus, checkOutAt: string | null): Presence {
  switch (status) {
    case 'Present': return { kind: 'present' };
    case 'Departed': return { kind: 'departed', at: checkOutAt ?? '' };
    case 'NotifiedAbsence': return { kind: 'absenceReported' };
    default: return { kind: 'notArrived' };
  }
}
